/**
 * Preisautomatik-Schicht über Grundkalender und Monat – nur aus vorhandenen Daten
 * (`dynamic-pricing/calendar`, `dynamic-pricing/overview`): Ziel für Preis und Mindestnächte, solange
 * es vom gültigen Wert abweicht, die Marke der Automatik-Übertragung und der Modus je Apartment.
 */
import type {
  DynamicPricingCalendarApartment,
  DynamicPricingCalendarDay,
  DynamicPricingCalendarResponse,
  DynamicPricingMode,
  DynamicPricingOverviewResponse,
} from '@/shared/api';

export interface AutomationCell {
  /** Ziel der Automatik, nur wenn es vom gültigen Preis abweicht. */
  targetRate: number | null;
  targetMinimumStay: number | null;
  /** ↗ gesendet, Bestätigung offen · ! fehlgeschlagen · null bestätigt oder nichts gesendet. */
  publication: 'sent' | 'failed' | null;
}

export interface AutomationLayer {
  apartment: (propertyId: string) => DynamicPricingCalendarApartment | null;
  mode: (propertyId: string) => DynamicPricingMode | null;
  day: (propertyId: string, date: string) => DynamicPricingCalendarDay | null;
  cell: (propertyId: string, date: string) => AutomationCell | null;
}

export const MODE_LABEL: Record<DynamicPricingMode, string> = {
  Automatic: 'Vollautomatisch',
  MinimumStayOnly: 'Nur Mindestaufenthalt',
  Off: 'Aus',
};

export function automationCell(day: DynamicPricingCalendarDay): AutomationCell {
  const rate = day.currentRate ?? day.confirmedRate;
  const minimumStay = day.currentMinimumStay ?? day.confirmedMinimumStay;
  const status = day.publication?.status;
  return {
    targetRate: day.desiredRate !== null && day.desiredRate !== rate ? day.desiredRate : null,
    targetMinimumStay:
      day.desiredMinimumStay !== null && day.desiredMinimumStay !== minimumStay
        ? day.desiredMinimumStay
        : null,
    publication:
      !status || status === 'Confirmed'
        ? null
        : status === 'Failed' || status === 'ConfirmationFailed'
          ? 'failed'
          : 'sent',
  };
}

export function buildAutomationLayer(
  calendar: DynamicPricingCalendarResponse | undefined,
  overview: DynamicPricingOverviewResponse | undefined,
): AutomationLayer {
  const days = new Map<string, DynamicPricingCalendarDay>();
  for (const apartment of calendar?.apartments ?? []) {
    for (const day of apartment.days) days.set(`${apartment.propertyId}:${day.date}`, day);
  }
  const modes = new Map(overview?.apartments.map((entry) => [entry.propertyId, entry.mode]));
  const day = (propertyId: string, date: string) => days.get(`${propertyId}:${date}`) ?? null;
  const apartments = new Map(calendar?.apartments.map((entry) => [entry.propertyId, entry]));
  return {
    apartment: (propertyId) => apartments.get(propertyId) ?? null,
    mode: (propertyId) => modes.get(propertyId) ?? null,
    day,
    cell: (propertyId, date) => {
      const found = day(propertyId, date);
      return found ? automationCell(found) : null;
    },
  };
}

/**
 * Ein von Hand gesetzter Wert hat kein Ziel der Automatik mehr – sie sendet dort nichts. Die Quelle
 * kommt aus dem V&V-Lesemodell, das nach einer Übertragung zuerst aktuell ist.
 */
export function ownedCell(
  cell: AutomationCell | null,
  day: { rateSource: string; minimumStaySource: string },
): AutomationCell | null {
  if (!cell) return null;
  return {
    ...cell,
    targetRate: day.rateSource === 'Manual' ? null : cell.targetRate,
    targetMinimumStay: day.minimumStaySource === 'Manual' ? null : cell.targetMinimumStay,
  };
}
